import { useEffect, useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap, Observer } from '../../anim/gsapSetup'
import { Icon } from '../common/Icon'
import { stopScroll, startScroll } from '../../anim/smoothScroll'
import type { GalleryItem } from '../../types-gallery'

interface LightboxProps {
  items: GalleryItem[]
  index: number | null
  onClose: () => void
  onNavigate: (nextIndex: number) => void
}

export function Lightbox({ items, index, onClose, onNavigate }: LightboxProps) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const open = index != null
  const item = open ? items[index] : null

  const go = (delta: number) => {
    if (index == null) return
    onNavigate((index + delta + items.length) % items.length)
  }

  // open: scroll lock + focus + swipe + reveal
  useLayoutEffect(() => {
    if (!open) return
    registerGsap()
    stopScroll()
    document.body.style.overflow = 'hidden'
    const ctx = gsap.context(() => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      gsap.from('[data-lb="backdrop"]', { opacity: 0, duration: 0.3 })
      if (!reduced) gsap.from('[data-lb="figure"]', { opacity: 0, scale: 0.9, duration: 0.4, ease: 'power3.out' })
    }, ref)
    const observer = Observer.create({
      target: ref.current!,
      type: 'touch,pointer',
      onLeft: () => go(1),
      onRight: () => go(-1),
      tolerance: 60,
    })
    closeRef.current?.focus()
    return () => {
      ctx.revert()
      observer.kill()
      document.body.style.overflow = ''
      startScroll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // image swap animation on index change
  useLayoutEffect(() => {
    if (!open) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    gsap.fromTo('[data-lb="img"]', { opacity: 0.3, scale: 0.96 }, { opacity: 1, scale: 1, duration: 0.35, ease: 'power2.out' })
  }, [index, open])

  // keyboard
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'Tab') {
        // simple trap: keep focus on close button region
        e.preventDefault()
        closeRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index])

  if (!open || !item) return null

  return (
    <div ref={ref} className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={`${item.title}, image ${index + 1} of ${items.length}`}>
      <div data-lb="backdrop" onClick={onClose} className="absolute inset-0 bg-night/90 backdrop-blur-sm" />

      <button
        ref={closeRef}
        onClick={onClose}
        aria-label="Close gallery"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <Icon name="X" className="h-6 w-6" />
      </button>

      <button
        onClick={() => go(-1)}
        aria-label="Previous image"
        className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:left-6"
      >
        <Icon name="ChevronLeft" className="h-7 w-7" />
      </button>
      <button
        onClick={() => go(1)}
        aria-label="Next image"
        className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-6"
      >
        <Icon name="ChevronRight" className="h-7 w-7" />
      </button>

      <figure data-lb="figure" className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 md:p-16">
        <img
          data-lb="img"
          src={item.src}
          alt={item.title}
          className="max-h-[76vh] max-w-full rounded-lg object-contain shadow-2xl"
        />
        <figcaption className="text-center text-white">
          <p className="font-display text-lg font-semibold">{item.title}</p>
          {item.caption && <p className="mt-1 text-sm text-white/60">{item.caption}</p>}
          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-white/40">
            {index + 1} / {items.length}
          </p>
        </figcaption>
      </figure>
    </div>
  )
}
